import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCoins, FaEdit, FaTrash, FaSave, FaTimes, FaEye, FaPlus } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const COIN_PACKS = {
  starter:  { id: 'starter',  label: 'Pack Starter',  coins: 500,   price: 500  },
  standard: { id: 'standard', label: 'Pack Standard', coins: 1500,  price: 1000 },
  pro:      { id: 'pro',      label: 'Pack Pro',      coins: 4000,  price: 2500 },
  mega:     { id: 'mega',     label: 'Pack Méga',     coins: 10000, price: 5000 },
};

const SHOP_ITEMS = {
  lives_3:   { id: 'lives_3',   label: '+3 Vies',     price: 100 },
  hints_5:   { id: 'hints_5',   label: 'x5 Indices',  price: 150 },
  double_xp: { id: 'double_xp', label: 'XP x2 (1h)', price: 200 },
};

function AdminShop() {
  const [activeTab, setActiveTab] = useState('packs');
  const [packs, setPacks] = useState([]);
  const [items, setItems] = useState(Object.values(SHOP_ITEMS));
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPackData, setNewPackData] = useState({ label: '', coins: '', price: '', id: '' });
  const [loading, setLoading] = useState(false);

  // Charger les packs au démarrage
  useEffect(() => {
    loadPacks();
  }, []);

  const loadPacks = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/shop/packs');
      setPacks(data.packs || []);
    } catch (err) {
      console.error('Erreur loadPacks :', err);
      toast.error('Erreur lors du chargement des packs');
    } finally {
      setLoading(false);
    }
  };

  // Édition pack
  const startEditPack = (pack) => {
    setEditingId(pack.id);
    setFormData({ ...pack });
  };

  const savePack = async (id) => {
    if (!formData.label || !formData.coins || !formData.price) {
      toast.error('Tous les champs sont requis');
      return;
    }
    try {
      const { data } = await api.put(`/shop/packs/${id}`, {
        label: formData.label,
        coins: parseInt(formData.coins),
        price: parseInt(formData.price)
      });
      setPacks(packs.map(p => p.id === id ? data.pack : p));
      setEditingId(null);
      toast.success('Pack modifié !');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la modification');
    }
  };

  // Édition item
  const startEditItem = (item) => {
    setEditingId(item.id);
    setFormData({ ...item });
  };

  const saveItem = (id) => {
    if (!formData.label || !formData.price) {
      toast.error('Tous les champs sont requis');
      return;
    }
    setItems(items.map(i => i.id === id ? formData : i));
    setEditingId(null);
    toast.success('Article modifié !');
  };

  const deletePack = async (id) => {
    try {
      await api.delete(`/shop/packs/${id}`);
      setPacks(packs.filter(p => p.id !== id));
      toast.success('Pack supprimé !');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const deleteItem = (id) => {
    setItems(items.filter(i => i.id !== id));
    toast.success('Article supprimé !');
  };

  const addNewPack = async () => {
    if (!newPackData.id || !newPackData.label || !newPackData.coins || !newPackData.price) {
      toast.error('Tous les champs sont requis');
      return;
    }
    try {
      const { data } = await api.post('/shop/packs', {
        id: newPackData.id.toLowerCase(),
        label: newPackData.label,
        coins: parseInt(newPackData.coins),
        price: parseInt(newPackData.price),
      });
      setPacks([...packs, data.pack]);
      setNewPackData({ label: '', coins: '', price: '', id: '' });
      setShowAddModal(false);
      toast.success('Pack créé !');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la création');
    }
  };

  const calculatePricePerCoin = (coins, price) => {
    return (price / coins).toFixed(3);
  };

  return (
    <div className="min-h-screen bg-qfl-dark p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-black text-qfl-gold mb-2" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            🛍️ GESTION BOUTIQUE
          </h1>
          <p className="text-white/50">Gérez les packs de pièces et les articles</p>
        </div>

        {/* Tabs + Bouton ajouter */}
        <div className="flex gap-2 mb-8 justify-between items-center">
          <div className="flex gap-2">
            <motion.button
              onClick={() => setActiveTab('packs')}
              className={`px-6 py-2 rounded-lg font-bold transition-all ${
                activeTab === 'packs'
                  ? 'bg-qfl-gold text-qfl-dark'
                  : 'bg-qfl-card text-white/60 hover:bg-white/20'
              }`}
            >
              <FaCoins className="inline mr-2" />
              Packs de Pièces
            </motion.button>
            <motion.button
              onClick={() => setActiveTab('items')}
              className={`px-6 py-2 rounded-lg font-bold transition-all ${
                activeTab === 'items'
                  ? 'bg-qfl-gold text-qfl-dark'
                  : 'bg-qfl-card text-white/60 hover:bg-white/20'
              }`}
            >
              <FaEye className="inline mr-2" />
              Articles Boutique
            </motion.button>
          </div>

          {activeTab === 'packs' && (
            <motion.button
              onClick={() => setShowAddModal(true)}
              className="bg-qfl-gold hover:opacity-80 text-qfl-dark px-6 py-2 rounded-lg font-bold transition-all flex items-center gap-2"
            >
              <FaPlus size={16} /> Ajouter un Pack
            </motion.button>
          )}
        </div>

        {/* PACKS DE PIÈCES */}
        <AnimatePresence mode="wait">
          {activeTab === 'packs' && (
            <motion.div key="packs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="grid gap-4">
                {packs.map((pack) => (
                  <motion.div
                    key={pack.id}
                    className="bg-qfl-card border border-qfl-gold/30 rounded-xl p-6 hover:border-qfl-gold/50 transition-all"
                  >
                    {editingId === pack.id ? (
                      // Mode édition
                      <div className="grid grid-cols-4 gap-4">
                        <input
                          type="text"
                          value={formData.label || ''}
                          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                          className="bg-white/5 border border-qfl-gold/30 rounded-lg px-3 py-2 text-white placeholder-white/30"
                          placeholder="Label"
                        />
                        <input
                          type="number"
                          value={formData.coins || ''}
                          onChange={(e) => setFormData({ ...formData, coins: parseInt(e.target.value) })}
                          className="bg-white/5 border border-qfl-gold/30 rounded-lg px-3 py-2 text-white placeholder-white/30"
                          placeholder="Pièces"
                        />
                        <input
                          type="number"
                          value={formData.price || ''}
                          onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) })}
                          className="bg-white/5 border border-qfl-gold/30 rounded-lg px-3 py-2 text-white placeholder-white/30"
                          placeholder="Prix FCFA"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => savePack(pack.id)}
                            className="flex-1 bg-qfl-green hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
                          >
                            <FaSave size={14} /> Sauver
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="flex-1 bg-qfl-red hover:bg-red-700 text-white px-4 py-2 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
                          >
                            <FaTimes size={14} /> Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Mode affichage
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-qfl-gold mb-2">{pack.label}</h3>
                          <div className="flex gap-6 text-sm text-white/60">
                            <span>🪙 <strong className="text-white">{pack.coins.toLocaleString()}</strong> pièces</span>
                            <span>💰 <strong className="text-white">{pack.price.toLocaleString()}</strong> FCFA</span>
                            <span>📊 <strong className="text-qfl-gold">{calculatePricePerCoin(pack.coins, pack.price)}</strong> FCFA/pièce</span>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => startEditPack(pack)}
                            className="bg-qfl-gold hover:opacity-80 text-qfl-dark p-3 rounded-lg transition-all font-bold"
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            onClick={() => deletePack(pack.id)}
                            className="bg-qfl-red hover:opacity-80 text-white p-3 rounded-lg transition-all font-bold"
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ARTICLES BOUTIQUE */}
          {activeTab === 'items' && (
            <motion.div key="items" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="grid gap-4">
                {items.map((item) => (
                  <motion.div
                    key={item.id}
                    className="bg-qfl-card border border-qfl-gold/30 rounded-xl p-6 hover:border-qfl-gold/50 transition-all"
                  >
                    {editingId === item.id ? (
                      // Mode édition
                      <div className="grid grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={formData.label || ''}
                          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                          className="bg-white/5 border border-qfl-gold/30 rounded-lg px-3 py-2 text-white placeholder-white/30"
                          placeholder="Label"
                        />
                        <input
                          type="number"
                          value={formData.price || ''}
                          onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) })}
                          className="bg-white/5 border border-qfl-gold/30 rounded-lg px-3 py-2 text-white placeholder-white/30"
                          placeholder="Prix (pièces)"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveItem(item.id)}
                            className="flex-1 bg-qfl-green hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
                          >
                            <FaSave size={14} /> Sauver
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="flex-1 bg-qfl-red hover:bg-red-700 text-white px-4 py-2 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
                          >
                            <FaTimes size={14} /> Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Mode affichage
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-qfl-gold mb-2">{item.label}</h3>
                          <p className="text-sm text-white/60">💰 Prix : <strong className="text-white">{item.price.toLocaleString()}</strong> pièces</p>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => startEditItem(item)}
                            className="bg-qfl-gold hover:opacity-80 text-qfl-dark p-3 rounded-lg transition-all font-bold"
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            onClick={() => deleteItem(item.id)}
                            className="bg-qfl-red hover:opacity-80 text-white p-3 rounded-lg transition-all font-bold"
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modale ajouter un pack */}
        <AnimatePresence>
          {showAddModal && (
            <motion.div
              className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAddModal(false)}
            >
              <motion.div
                className="bg-qfl-dark border border-qfl-gold/30 rounded-xl p-8 max-w-md w-full"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="text-2xl font-bold text-qfl-gold mb-6">Créer un Nouveau Pack</h2>

                <div className="space-y-4">
                  <div>
                    <label className="text-white/70 text-sm block mb-2">ID (ex: premium)</label>
                    <input
                      type="text"
                      value={newPackData.id}
                      onChange={(e) => setNewPackData({ ...newPackData, id: e.target.value })}
                      className="w-full bg-white/5 border border-qfl-gold/30 rounded-lg px-3 py-2 text-white placeholder-white/30"
                      placeholder="premium"
                    />
                  </div>

                  <div>
                    <label className="text-white/70 text-sm block mb-2">Nom du Pack</label>
                    <input
                      type="text"
                      value={newPackData.label}
                      onChange={(e) => setNewPackData({ ...newPackData, label: e.target.value })}
                      className="w-full bg-white/5 border border-qfl-gold/30 rounded-lg px-3 py-2 text-white placeholder-white/30"
                      placeholder="Pack Premium"
                    />
                  </div>

                  <div>
                    <label className="text-white/70 text-sm block mb-2">Nombre de Pièces</label>
                    <input
                      type="number"
                      value={newPackData.coins}
                      onChange={(e) => setNewPackData({ ...newPackData, coins: e.target.value })}
                      className="w-full bg-white/5 border border-qfl-gold/30 rounded-lg px-3 py-2 text-white placeholder-white/30"
                      placeholder="2500"
                    />
                  </div>

                  <div>
                    <label className="text-white/70 text-sm block mb-2">Prix (FCFA)</label>
                    <input
                      type="number"
                      value={newPackData.price}
                      onChange={(e) => setNewPackData({ ...newPackData, price: e.target.value })}
                      className="w-full bg-white/5 border border-qfl-gold/30 rounded-lg px-3 py-2 text-white placeholder-white/30"
                      placeholder="1500"
                    />
                  </div>

                  {newPackData.coins && newPackData.price && (
                    <div className="bg-qfl-gold/10 border border-qfl-gold/30 rounded-lg p-3 mt-4">
                      <p className="text-qfl-gold text-sm">
                        📊 Prix: <strong>{calculatePricePerCoin(parseInt(newPackData.coins), parseInt(newPackData.price))}</strong> FCFA/pièce
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={addNewPack}
                    className="flex-1 bg-qfl-gold hover:opacity-80 text-qfl-dark px-4 py-2 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
                  >
                    <FaSave size={14} /> Créer
                  </button>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-qfl-red hover:opacity-80 text-white px-4 py-2 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
                  >
                    <FaTimes size={14} /> Annuler
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default AdminShop;
